/**
 * Authentication context for managing user state.
 * Provides login, logout, and role-based access control.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api, ApiRequestError } from '@/lib/api'

export type UserRole = 'admin' | 'app_owner'

export interface User {
  id: number
  email: string
  name: string
  role: UserRole
}

export interface LoginCredentials {
  email: string
  password: string
}

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
  hasRole: (role: UserRole) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'

// Generic error message for security (Property 1)
const GENERIC_AUTH_ERROR = 'Invalid email or password'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Load user from storage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem(USER_KEY)
    const storedToken = localStorage.getItem(TOKEN_KEY)

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        // Invalid stored data, clear it
        localStorage.removeItem(USER_KEY)
        localStorage.removeItem(TOKEN_KEY)
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await api.post<{ user: User; token: string }>('/auth/login', credentials)

      const { user: userData, token } = response.data

      // Store token and user
      localStorage.setItem(TOKEN_KEY, token)
      localStorage.setItem(USER_KEY, JSON.stringify(userData))

      setUser(userData)
    } catch (error) {
      // Always throw generic error message for security (Property 1)
      if (error instanceof ApiRequestError) {
        throw new Error(GENERIC_AUTH_ERROR)
      }
      throw new Error(GENERIC_AUTH_ERROR)
    }
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    setUser(null)
  }

  const hasRole = (role: UserRole): boolean => {
    if (!user) return false
    // Admin has all permissions
    if (user.role === 'admin') return true
    return user.role === role
  }

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper to get the stored token for API requests
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}
